import { useState, useEffect } from "react";
import { useAtomValue } from "jotai";
import { filteredTasksAtom } from "../store/atoms/derivedAtoms.js";
import { currentProjectIdAtom } from "../store/atoms/taskAtoms.js";
import "../styles/AddTaskForm.css";
import {
  useTaskForm,
  useUser,
  useMenu,
  useTaskActions,
} from "../hooks/index.js";

const AddTaskForm = () => {
  const {
    taskName,
    setTaskName,
    description,
    setDescription,
    category,
    setCategory,
    effort,
    setEffort,
    impact,
    setImpact,
    dueDate,
    setDueDate,
    clearForm,
  } = useTaskForm();
  const { preferences } = useUser();
  const { handleOverlayClose } = useMenu();
  const { addTask } = useTaskActions();
  const currentProjectId = useAtomValue(currentProjectIdAtom);
  const availableTasks = useAtomValue(filteredTasksAtom);
  const [parentId, setParentId] = useState(currentProjectId);

  useEffect(() => {
    setParentId(currentProjectId);
  }, [currentProjectId]);

  const quickAddTask = async (e) => {
    e.preventDefault();
    if (!taskName) {
      alert("Task Name is required!");
      return;
    }

    try {
      const taskId = await addTask({
        taskName,
        effort: effort || 50,
        impact: impact || 50,
        dueDate: dueDate || null,
        description: description || null,
        category: category || null,
        parentId: parentId,
      });

      if (taskId) {
        clearForm();
        handleOverlayClose();
      }
    } catch (error) {
      console.error("Error adding task:", error);
    }
  };

  return (
    <div>
      <h2>Add A New Task</h2>
      <form onSubmit={quickAddTask} className="add-task-form">
        {/* Task Name */}
        <label>
          Task:
          <input
            type="text"
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            required
          />
        </label>

        {/* Description */}
        <label>
          Description:
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows="2"
            placeholder="Add additional details about the task..."
          />
        </label>

        {!currentProjectId ? (
          <label>
            Parent Project (optional):
            <select
              value={parentId || ""}
              onChange={(e) =>
                setParentId(e.target.value ? parseInt(e.target.value) : null)
              }
            >
              <option value="">No parent (top-level task)</option>
              {availableTasks.map((task) => (
                <option key={task.task_id} value={task.task_id}>
                  {task.task_name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {/* Category */}
        <label>
          Category:
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {preferences.map((pref) => (
              <option key={pref.category} value={pref.category}>
                {pref.name
                  ? `${pref.name} (${pref.color})`
                  : `Not Set (${pref.color})`}
              </option>
            ))}
          </select>
        </label>

        {/* Effort */}
        <label>
          Effort:
          <div className="radio-group">
            {[
              { label: "L", full: "Low", value: 20 },
              { label: "M", full: "Medium", value: 40 },
              { label: "H", full: "High", value: 60 },
              { label: "T", full: "Taxing", value: 80 },
            ].map((option) => (
              <label key={option.value} title={option.full}>
                <input
                  type="radio"
                  name="effort"
                  value={option.value}
                  checked={effort === option.value}
                  onChange={() => setEffort(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </label>

        {/* Impact */}
        <label>
          Impact:
          <div className="radio-group">
            {[
              { label: "L", full: "Limited", value: 20 },
              { label: "M", full: "Moderate", value: 40 },
              { label: "H", full: "High", value: 60 },
              { label: "T", full: "Transformative", value: 80 },
            ].map((option) => (
              <label key={option.value} title={option.full}>
                <input
                  type="radio"
                  name="impact"
                  value={option.value}
                  checked={impact === option.value}
                  onChange={() => setImpact(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </label>

        {/* Deadline */}
        <label>
          Deadline:
          <input
            type="date"
            value={dueDate || ""}
            onChange={(e) => setDueDate(e.target.value || null)}
          />
        </label>

        {/* Buttons */}
        <div className="form-buttons">
          <button type="submit">Save Task</button>
          <button type="button" onClick={handleOverlayClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddTaskForm;
